import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI, storesAPI, bulkAPI } from '../services/api';
import { notify } from '../utils/notify';
import { HiSearch, HiFilter, HiStar, HiShoppingCart, HiSparkles } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { PRODUCT_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import { sourceLabel } from '../utils/sourceLabel';
import { mensajeDeError } from '../utils/apiError';
import ProductDetailsModal from '../components/ProductDetailsModal';
import { desdeCandidato } from '../utils/productoVista';
import { comisionDe } from '../utils/comision';
import { calcularEconomia, dinero, MARGEN_POR_DEFECTO } from '../utils/economia';
import { tipoDeFuente, sePuedePublicar, destinosPosibles } from '../utils/compatibilidad';

export default function ProductSearch() {
    // Load saved search from sessionStorage
    const savedSearch = JSON.parse(sessionStorage.getItem('productSearch') || '{}');
    const { t } = useTranslation();


    const [query, setQuery] = useState(savedSearch.query || '');
    const [source, setSource] = useState(savedSearch.source || 'all');
    const [products, setProducts] = useState(savedSearch.products || []);
    const [sourceStatus, setSourceStatus] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stores, setStores] = useState([]);
    const [tiendasLeidas, setTiendasLeidas] = useState(false);
    const [importingProducts, setImportingProducts] = useState(new Set());

    // Selección múltiple. Importar de uno en uno cuesta dos consultas al
    // proveedor por producto —ficha y flete—, y el proveedor admite una por
    // segundo: una tanda de veinte es un minuto largo. Por eso no se hace
    // esperando delante de la pantalla, sino como operación en segundo plano,
    // que es justo para lo que existe la página de Operaciones Masivas.
    const [seleccion, setSeleccion] = useState(new Set());
    const [enviandoTanda, setEnviandoTanda] = useState(false);

    const alternarSeleccion = (externalId) =>
        setSeleccion((antes) => {
            const copia = new Set(antes);
            if (copia.has(externalId)) copia.delete(externalId);
            else copia.add(externalId);
            return copia;
        });

    const importarSeleccionados = async () => {
        if (!tiendasLeidas) {
            notify.error(t('product_search.stores_unknown'));
            fetchStores();
            return;
        }
        if (stores.length === 0) {
            notify.error(t('product_search.connect_store_first'));
            return;
        }

        const elegidos = products.filter((p) => seleccion.has(p.externalId));
        if (elegidos.length === 0) return;

        setEnviandoTanda(true);
        try {
            await bulkAPI.bulkImport({
                storeId: stores[0].id,
                products: elegidos.map((p) => ({
                    productData: p,
                    ...(p.warehouse ? { warehouse: p.warehouse } : {})
                }))
            });
            setSeleccion(new Set());
            notify.success(t('product_search.bulk_started', { count: elegidos.length }));
        } catch (error) {
            notify.error(mensajeDeError(error, t('product_search.bulk_failed')));
        } finally {
            setEnviandoTanda(false);
        }
    };
    const [filters, setFilters] = useState(savedSearch.filters || {
        minPrice: '',
        maxPrice: '',
        minRating: '',
        warehouse: '',
        sinVariantes: false
    });
    const [showFilters, setShowFilters] = useState(false);
    const [vistaPrevia, setVistaPrevia] = useState(null);
    // Lo que vamos averiguando sobre las variantes, sin tocar los resultados
    // originales: {externalId: numeroDeVariantes}
    const [variantesPorProducto, setVariantesPorProducto] = useState({});
    const [comprobandoVariantes, setComprobandoVariantes] = useState(0);

    // Fetch stores on mount
    useEffect(() => {
        fetchStores();
    }, []);

    // Si la lista de tiendas no llega, NO es que no tengas ninguna: es que no
    // se ha podido leer. Sin esta distincion, importar te decia «conecta una
    // tienda primero» teniendo la tuya conectada y funcionando.
    const fetchStores = async () => {
        try {
            const response = await storesAPI.getAll();
            if (response.data.success) {
                setStores(response.data.data.stores || []);
                setTiendasLeidas(true);
            }
        } catch (error) {
            setTiendasLeidas(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) {
            notify.error(t('product_search.enter_search_term'));
            return;
        }

        setLoading(true);
        try {
            // `sinVariantes` no viaja al servidor: el proveedor no sabe
            // responder a eso, y el filtro se aplica aquí sobre lo que ya
            // hemos comprobado.
            const { sinVariantes, ...filtrosDeServidor } = filters;

            const response = await productsAPI.search({
                q: query,
                source,
                ...filtrosDeServidor
            });
            const searchResults = response.data.data.products;
            setProducts(searchResults);
            setSourceStatus(response.data.data.sources || []);

            // Save search to sessionStorage for persistence
            sessionStorage.setItem('productSearch', JSON.stringify({
                query,
                source,
                filters,
                products: searchResults
            }));

            if (searchResults.length === 0) {
                notify.info(t('product_search.no_products_found'));
            }
        } catch (error) {
            notify.error(mensajeDeError(error, t('product_search.search_failed')));
        } finally {
            setLoading(false);
        }
    };

    // Cuántas variantes tiene un producto: lo que trajo la búsqueda, o lo que
    // hayamos averiguado después.
    const variantesDe = (product) =>
        variantesPorProducto[product.externalId] ?? product.variantCount ?? null;

    // Con la casilla puesta, se pregunta por los que faltan. De uno en uno,
    // porque el proveedor admite una consulta por segundo y ese turno lo
    // comparten todos los usuarios de la aplicación.
    useEffect(() => {
        if (!filters.sinVariantes) return;

        const pendientes = products.filter(
            (p) => p.source === 'cj' && variantesDe(p) === null
        );

        if (pendientes.length === 0) {
            setComprobandoVariantes(0);
            return;
        }

        let vigente = true;
        setComprobandoVariantes(pendientes.length);

        (async () => {
            for (const p of pendientes) {
                if (!vigente) return;

                try {
                    const res = await productsAPI.getDetail({
                        source: p.source,
                        externalId: p.externalId,
                        ...(p.warehouse ? { warehouse: p.warehouse } : {})
                    });

                    const detalle = res.data.data;
                    if (!vigente) return;

                    if (detalle?.detalleDisponible && Array.isArray(detalle.variantes)) {
                        setVariantesPorProducto((actual) => ({
                            ...actual,
                            [p.externalId]: detalle.variantes.length
                        }));
                    }
                } catch {
                    // Un producto que no se pueda comprobar se queda como
                    // "sin comprobar". No se descarta ni se da por bueno.
                }

                if (vigente) setComprobandoVariantes((n) => Math.max(0, n - 1));
            }
        })();

        return () => {
            vigente = false;
        };
    }, [filters.sinVariantes, products]);

    // Los resultados que tu tienda no admite como origen.
    const noPublicables = (() => {
        const plataforma = stores[0]?.platform;
        if (!plataforma) return 0;
        return products.filter((p) => !sePuedePublicar(p.source, plataforma).permitido).length;
    })();

    const handleImport = async (product, variantId = null) => {
        if (!tiendasLeidas) {
            notify.error(t('product_search.stores_unknown'));
            fetchStores();
            return;
        }
        // Check if there are connected stores
        if (stores.length === 0) {
            notify.error(t('product_search.connect_store_first'));
            return;
        }

        // Mark product as importing
        setImportingProducts(prev => new Set([...prev, product.externalId]));

        try {
            const response = await productsAPI.import({
                productData: product,
                storeId: stores[0].id, // Use first available store
                // Desde la tarjeta no se ha elegido variante: el servidor toma
                // la mas barata con existencias.
                ...(variantId ? { variantId } : {})
            });

            if (response.data.success) {
                notify.success(t('product_search.import_success'));
            } else {
                notify.error(t('product_search.import_failed'));
            }
        } catch (error) {
            notify.error(mensajeDeError(error, t('product_search.import_failed')));
        } finally {
            setImportingProducts(prev => {
                const newSet = new Set(prev);
                newSet.delete(product.externalId);
                return newSet;
            });
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="page-title">{t('product_search.title')}</h1>
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                    {t('product_search.subtitle')}
                </p>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="card">
                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1 relative">
                        <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('product_search.search_placeholder')}
                            className="input pl-12 py-3"
                        />
                    </div>

                    <select
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        className="input w-full lg:w-48"
                    >
                        <option value="all">{t('product_search.all_sources')}</option>
                        {/* Cada fuente dice lo que es, porque de eso depende
                            dónde se puede revender lo que compres en ella. */}
                        {['cj', 'aliexpress', 'amazon', 'ebay'].map((f) => (
                            <option key={f} value={f}>
                                {sourceLabel(f)} · {t(`product_search.tipo_${tipoDeFuente(f)}`)}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`btn-secondary ${showFilters ? 'bg-primary-100 dark:bg-primary-900/30' : ''}`}
                    >
                        <HiFilter className="w-5 h-5 mr-2" />
                        {t('product_search.filters')}
                    </button>

                    <button type="submit" disabled={loading} className="btn-primary">
                        {loading ? (
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                        ) : (
                            <>
                                <HiSearch className="w-5 h-5 mr-2" />
                                {t('product_search.search_btn')}
                            </>
                        )}
                    </button>
                </div>

                {/* Filter Panel */}
                {showFilters && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <div>
                            {/* De dónde sale el paquete: decide los plazos que eBay
                                enseña al comprador. Solo lo aplica CJ. */}
                            <label className="label">{t('product_search.warehouse')}</label>
                            <select
                                value={filters.warehouse}
                                onChange={(e) => setFilters({ ...filters, warehouse: e.target.value })}
                                className="input"
                            >
                                <option value="">{t('product_search.warehouse_any')}</option>
                                <option value="US">{t('product_search.warehouse_us')}</option>
                                <option value="CN">{t('product_search.warehouse_cn')}</option>
                                <option value="DE">{t('product_search.warehouse_de')}</option>
                                <option value="GB">{t('product_search.warehouse_gb')}</option>
                            </select>
                            <p className="mt-1 text-xs text-slate-500">{t('product_search.warehouse_note')}</p>
                        </div>

                        <div>
                            <label className="label">{t('product_search.min_price')}</label>
                            <input
                                type="number"
                                value={filters.minPrice}
                                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                                placeholder="$0"
                                className="input"
                            />
                        </div>
                        <div>
                            <label className="label">{t('product_search.max_price')}</label>
                            <input
                                type="number"
                                value={filters.maxPrice}
                                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                                placeholder="$500"
                                className="input"
                            />
                        </div>
                        {/* Solo CJ declara variantes. De las demás fuentes no
                            sabemos si un producto tiene una o quince, así que la
                            casilla no aparece cuando no puede significar nada. */}
                        {(source === 'cj' || source === 'all') && (
                            <div className="col-span-2">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filters.sinVariantes}
                                        onChange={(e) => setFilters({ ...filters, sinVariantes: e.target.checked })}
                                        className="mt-1"
                                    />
                                    <span>
                                        <span className="label !mb-0">{t('product_search.only_single_variant')}</span>
                                        <span className="block text-xs text-slate-500">
                                            {t('product_search.only_single_variant_note')}
                                        </span>
                                    </span>
                                </label>
                            </div>
                        )}

                        {/* La calificacion minima solo la aplica Amazon. CJ ni
                            siquiera publica valoraciones, asi que en el resto de
                            fuentes el control estaba pintado y no descartaba
                            nada. */}
                        {(source === 'amazon' || source === 'all') && (
                            <div>
                                <label className="label">{t('product_search.min_rating')}</label>
                                <select
                                    value={filters.minRating}
                                    onChange={(e) => setFilters({ ...filters, minRating: e.target.value })}
                                    className="input"
                                >
                                    <option value="">{t('product_search.any_rating')}</option>
                                    <option value="3">3+ {t('product_search.stars')}</option>
                                    <option value="4">4+ {t('product_search.stars')}</option>
                                    <option value="4.5">4.5+ {t('product_search.stars')}</option>
                                </select>
                                <p className="mt-1 text-xs text-slate-500">{t('product_search.min_rating_note')}</p>
                            </div>
                        )}
                    </div>
                )}
            </form>

            {/* Cuando la casilla está puesta se avisa de lo que queda por
                comprobar: una lista que encoge sin explicación desconcierta. */}
            {filters.sinVariantes && comprobandoVariantes > 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('product_search.checking_variants', { n: comprobandoVariantes })}
                </p>
            )}

            {/* Qué hizo cada fuente. Sin esto, "no se encontraron productos" tapa
                por igual a la fuente sin credenciales, a la rota y a la que
                simplemente no tenía nada.

                Va ARRIBA, junto al buscador. Estaba debajo de la rejilla, o sea
                después de cuarenta tarjetas: lo único que dice por qué falta una
                fuente, colocado donde solo se ve por accidente. Con resultados
                delante no lo miraba nadie, que es como no tenerlo. Lo que
                informa sobre la búsqueda va donde está la búsqueda. */}
            {!loading && sourceStatus.length > 0 && (
                <div className="card mb-6 space-y-2">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        {sourceStatus.map((s) => (
                            <span key={s.source} className="flex items-center gap-1.5">
                                <span className="font-medium text-slate-700 dark:text-slate-200">
                                    {sourceLabel(s.source)}:
                                </span>
                                {s.configurada === false ? (
                                    <span className="text-slate-400">{t('product_search.source_not_configured')}</span>
                                ) : !s.ok ? (
                                    <span className="text-red-500">{t('product_search.source_error')}</span>
                                ) : (
                                    <span className={s.count > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}>
                                        {t('product_search.source_count', { count: s.count })}
                                    </span>
                                )}

                                {/* Si los resultados no salieron de lo que
                                    escribiste, se dice. Cambiar en silencio el
                                    término de alguien es exactamente lo que
                                    este proyecto no hace. */}
                                {s.terminoUsado && (
                                    <span className="text-slate-500 dark:text-slate-400">
                                        {t('product_search.searched_as', { termino: s.terminoUsado })}
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>

                    {sourceStatus.some((s) => s.terminoUsado) && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {t('product_search.english_only_source')}
                        </p>
                    )}

                    {/* No se pudo traducir. Callarlo dejaría un «0 productos»
                        que se lee como «esta fuente no lo tiene», cuando la
                        causa puede haber durado un segundo. Y las dos clases de
                        fallo piden cosas distintas: una se arregla esperando y
                        la otra no se arregla hasta mañana. */}
                    {sourceStatus.some((s) => s.traduccion) && (
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                            {t(
                                sourceStatus.some((s) => s.traduccion === 'sin_cuota_hoy')
                                    ? 'product_search.translation_no_quota'
                                    : 'product_search.translation_failed'
                            )}
                        </p>
                    )}

                    {sourceStatus.some((s) => !s.ok && s.configurada !== false) && (
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                            {sourceStatus.filter((s) => !s.ok && s.error).map((s) => `${sourceLabel(s.source)}: ${s.error}`).join(' · ')}
                        </p>
                    )}
                </div>
            )}

            {/* Cuántos de estos resultados no puedes vender.
                La advertencia por tarjeta ya existía; lo que faltaba era el
                total. Con un solo destino —eBay, que no admite revender de
                otro marketplace— este número es lo que mide cuánto catálogo se
                queda fuera, y por tanto cuándo hace falta una tienda propia. */}
            {products.length > 0 && noPublicables > 0 && (
                <p className="text-sm text-amber-700 dark:text-amber-300 -mt-2">
                    {t('product_search.not_publishable_count', {
                        count: noPublicables,
                        total: products.length,
                        store: sourceLabel(stores[0]?.platform)
                    })}
                </p>
            )}

            {/* Lo que se ha marcado, y qué hacer con ello. Va encima de la
                lista, que es donde estás mirando cuando terminas de marcar. */}
            {seleccion.size > 0 && (
                <div className="card flex flex-wrap items-center gap-3 !py-3 sticky top-20 z-20">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {t('product_search.selected', { count: seleccion.size })}
                    </span>
                    <button
                        type="button"
                        onClick={importarSeleccionados}
                        disabled={enviandoTanda}
                        className="btn-primary btn-sm"
                    >
                        <HiShoppingCart className="w-4 h-4 mr-1" />
                        {t('product_search.import_selected', { count: seleccion.size })}
                    </button>
                    <button
                        type="button"
                        onClick={() => setSeleccion(new Set())}
                        className="btn-secondary btn-sm"
                    >
                        {t('product_search.clear_selection')}
                    </button>
                    <Link to="/bulk" className="text-sm text-primary-500 hover:underline ml-auto">
                        {t('product_search.see_operations')}
                    </Link>
                </div>
            )}

            {/* Results */}
            {products.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {products
                        // Se ocultan los que SABEMOS que tienen varias. Los que
                        // no se han comprobado siguen a la vista, marcados: una
                        // lista limpia a base de descartar lo que no se ha
                        // mirado sería una lista falsa.
                        .filter((product) => !(filters.sinVariantes && variantesDe(product) > 1))
                        .map((product) => (
                        <div key={product.externalId} className="card-hover group overflow-hidden relative">
                            <label className="absolute top-3 left-3 z-10 flex items-center justify-center w-7 h-7 rounded-md bg-white/90 dark:bg-slate-900/90 border border-slate-300 dark:border-slate-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={seleccion.has(product.externalId)}
                                    onChange={() => alternarSeleccion(product.externalId)}
                                    aria-label={t('product_search.select_product')}
                                    className="w-4 h-4 accent-primary-500 cursor-pointer"
                                />
                            </label>
                            {/* Imagen. Es un botón: abre la ficha del producto sin
                                sacar al usuario de la aplicación ni abrir una
                                pestaña por cada producto que quiera mirar. */}
                            <div className="relative -mx-6 -mt-6 mb-4 aspect-square bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setVistaPrevia(product)}
                                    aria-label={t('product_search.open_details', { title: product.title })}
                                    className="block w-full h-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                                >
                                    <img
                                        src={product.mainImage || PRODUCT_IMAGE_FALLBACK}
                                        alt={product.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                    {/* Una función escondida no la usa nadie: al pasar
                                        por encima se ve que la imagen se puede pulsar. */}
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        <span className="flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-sm font-medium text-slate-900 shadow">
                                            <HiSearch className="w-4 h-4" />
                                            {t('product_search.see_details')}
                                        </span>
                                    </span>
                                </button>
                                {/* A la derecha, no a la izquierda: la casilla de
                                    selección está en `top-3 left-3` de la tarjeta y
                                    esta insignia estaba en `top-3 left-3` de la
                                    imagen, que por los márgenes negativos es el
                                    mismo punto. La casilla lleva `z-10`, así que
                                    tapaba el nombre de la tienda y se leía «azon». */}
                                <span className={`pointer-events-none absolute top-3 right-3 px-2 py-1 rounded-lg text-xs font-medium ${product.source === 'amazon' ? 'bg-orange-500 text-white' :
                                    product.source === 'aliexpress' ? 'bg-red-500 text-white' :
                                        'bg-purple-500 text-white'
                                    }`}>
                                    {product.source.charAt(0).toUpperCase() + product.source.slice(1)}
                                </span>
                            </div>

                            {/* Content */}
                            <div className="space-y-3">
                                <h3 className="font-medium text-slate-900 dark:text-white line-clamp-2 group-hover:text-primary-500">
                                    {product.title}
                                </h3>

                                {/* Lo que decide si un producto merece la pena no es su precio,
                                    sino lo que dejaría. Mismo desglose que en Mis Productos. */}
                                {(() => {
                                    // La tarjeta no puede pedir el envío de veinte
                                    // productos a la vez: el proveedor admite una
                                    // llamada por segundo. Así que dice lo que sabe
                                    // —la ganancia sin el envío— y la cifra completa
                                    // está a un clic, en la ficha.
                                    const cuenta = calcularEconomia({
                                        costoProducto: product.price,
                                        costoEnvio: null,
                                        margen: MARGEN_POR_DEFECTO,
                                        // El destino al que iría: el mismo que
                                        // usa el botón de importar.
                                        comision: comisionDe(stores[0]?.platform, stores[0]?.settings)
                                    });
                                    return (
                                        <div>
                                            <div className="grid grid-cols-3 gap-2 py-2 border-y border-slate-200 dark:border-slate-700">
                                                <div className="text-center">
                                                    <p className="text-[11px] uppercase tracking-wide text-slate-400">{t('imported_products.cost')}</p>
                                                    <p className="font-semibold text-slate-900 dark:text-white">{dinero(cuenta.costoProducto)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-[11px] uppercase tracking-wide text-slate-400">{t('imported_products.price')}</p>
                                                    <p className="font-semibold text-slate-900 dark:text-white">{dinero(cuenta.precioVenta)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-[11px] uppercase tracking-wide text-slate-400">{t('product_search.profit_no_shipping')}</p>
                                                    <p className="font-semibold text-green-500">{dinero(cuenta.ganancia)}</p>
                                                </div>
                                            </div>
                                            <p className="mt-1 text-[11px] text-slate-400">
                                                {t('product_search.margin_note', { margin: MARGEN_POR_DEFECTO })}
                                                {product.originalPrice > cuenta.costoProducto && (
                                                    <span className="ml-1 line-through">${product.originalPrice?.toFixed(2)}</span>
                                                )}
                                            </p>
                                        </div>
                                    );
                                })()}

                                {product.warehouse && (
                                    <p className="text-xs text-slate-500">
                                        {t('product_search.ships_from', { country: product.warehouse })}
                                    </p>
                                )}

                                {/* Avisar antes de importar, no después de
                                    intentar publicar: la política de eBay no
                                    admite revender lo comprado en otro
                                    marketplace, pero tu propia tienda sí. */}
                                {(() => {
                                    const plataforma = stores[0]?.platform;
                                    if (!plataforma) return null;

                                    const { permitido } = sePuedePublicar(product.source, plataforma);
                                    if (permitido) return null;

                                    // Las salidas que existen HOY. Antes salían
                                    // también Shopify y WooCommerce —que no se
                                    // pueden conectar— y encima con su nombre
                                    // en crudo y en minúscula, sin pasar por
                                    // `sourceLabel` como la línea de al lado.
                                    const otras = destinosPosibles(product.source);
                                    return (
                                        <p className="text-xs text-amber-700 dark:text-amber-300">
                                            {t('product_search.not_publishable', { store: sourceLabel(plataforma) })}
                                            {otras.length > 0
                                                ? ' ' + t('product_search.publishable_in', { stores: otras.map(sourceLabel).join(', ') })
                                                : ' ' + t('product_search.publishable_nowhere')}
                                        </p>
                                    );
                                })()}

                                {product.source === 'cj' && (() => {
                                    const n = variantesDe(product);
                                    if (n === null) {
                                        return (
                                            <p className="text-xs text-slate-400">
                                                {t('product_search.variants_unknown')}
                                            </p>
                                        );
                                    }
                                    return (
                                        <p className={`text-xs ${n > 1 ? 'text-slate-500' : 'text-green-600 dark:text-green-400'}`}>
                                            {n === 1
                                                ? t('product_search.variants_one')
                                                : t('product_search.variants_many', { n })}
                                        </p>
                                    );
                                })()}

                                {/* La valoración y las reseñas solo cuando las
                                    hay. El `|| 0` de las reseñas escribía «0
                                    reseñas» para fuentes que sencillamente no
                                    las publican, que es distinto de tener cero.
                                    Y el bloque entero desaparece cuando no se
                                    sabe ninguna de las dos: dos «N/A» seguidos
                                    no informan, solo ocupan. */}
                                {(product.rating != null || product.reviewsCount != null) && (
                                    <div className="flex items-center gap-3 text-sm text-slate-500">
                                        {product.rating != null && (
                                            <span className="flex items-center gap-1">
                                                <HiStar className="w-4 h-4 text-amber-500" />
                                                {product.rating.toFixed(1)}
                                            </span>
                                        )}
                                        {product.reviewsCount != null && (
                                            <span>
                                                {product.reviewsCount.toLocaleString()} {t('product_search.reviews')}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* El mismo campo significa cosas distintas
                                    según de dónde venga, y la etiqueta era
                                    siempre la de CJ:

                                      CJ          vendedores que lo listan
                                      AliExpress  unidades vendidas

                                    Llamar «vendedores» a unas ventas invierte
                                    justo el dato que más pesa al decidir en un
                                    marketplace. Amazon y eBay ya no mandan nada
                                    aquí, porque no lo saben. */}
                                {product.salesCount > 0 && (
                                    <p className="text-sm text-slate-500">
                                        {product.source === 'cj'
                                            ? t('product_search.listed_by_sellers', { n: product.salesCount.toLocaleString() })
                                            : t('product_search.units_sold', { n: product.salesCount.toLocaleString() })}
                                    </p>
                                )}

                                <div className="flex gap-2 pt-2">
                                    <button
                                        onClick={() => handleImport(product)}
                                        className="btn-primary btn-sm flex-1"
                                    >
                                        <HiShoppingCart className="w-4 h-4 mr-1" />
                                        {t('product_search.import')}
                                    </button>
                                    {/* El nombre de la tienda dice a dónde lleva; la lupa, que es para mirarlo. */}
                                    <a
                                        href={product.supplierUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={t('product_search.view_on', { source: sourceLabel(product.source) })}
                                        className="btn-secondary btn-sm flex-1 justify-center"
                                    >
                                        <HiSearch className="w-4 h-4 mr-1" />
                                        {sourceLabel(product.source)}
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty State */}
            {!loading && products.length === 0 && query && (
                <div className="card text-center py-16">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiSearch className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">{t('product_search.no_products_found_title')}</h3>
                    <p className="text-slate-500">{t('product_search.no_products_found_desc')}</p>
                </div>
            )}

            {/* Ficha del producto, antes de importarlo */}
            {vistaPrevia && (
                <ProductDetailsModal
                    vista={desdeCandidato(vistaPrevia)}
                    comision={comisionDe(stores[0]?.platform, stores[0]?.settings)}
                    importing={importingProducts.has(vistaPrevia.externalId)}
                    onImport={handleImport}
                    onClose={() => setVistaPrevia(null)}
                />
            )}

            {/* Initial State */}
            {!loading && products.length === 0 && !query && (
                <div className="card text-center py-16">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiSparkles className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">{t('product_search.start_searching_title')}</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                        {t('product_search.start_searching_desc')}
                    </p>
                </div>
            )}
        </div>
    );
}
